import { Button, Col, Flex, Icon, Row, Text } from '@wyz/ui';
import { useConfig, useLocale, Trans } from '~/common/context';
import { IQuote } from '~/purchase/types';
import { QuoteActions } from '~/purchase/components';
import { isNil } from 'lodash-es';
import { useState } from 'react';
import { PdfModal } from '~/common/components';

interface ExpandedRowServicesOnlyProps {
  quote: IQuote;
  selectedCustomerCode: string;
}

export const ExpandedRowServicesOnly = ({
  quote,
  selectedCustomerCode,
}: ExpandedRowServicesOnlyProps) => {
  const { apiUrl, tenantCode } = useConfig();
  const locale = useLocale();
  const hasDiagnosis = quote.express && !isNil(quote.diagnosisId);
  const [pdf, setPdf] = useState<string | null>(null);

  return (
    <>
      <Row className='m-0 mb-section'>
        <Col lg={8}>
          <Text weight='bold' color='secondary-gray'>
            <Trans i18nKey='app.quote.history.table.sub_row.services' />
          </Text>
        </Col>
      </Row>
      <Row className='m-0 mb-4'>
        <Col lg={8}>
          <div className='bg-white h-100'>
            <table cellSpacing={0} cellPadding={0} className='nested w-100'>
              <thead>
                <tr>
                  {hasDiagnosis && (
                    <th className='text-left' style={{ width: '15%' }}>
                      <Trans i18nKey='app.quote.history.table.sub_row.diagnosis' />
                    </th>
                  )}
                  <th className='text-left' style={{ width: '20%' }}>
                    <Trans i18nKey='app.common.service.quote.code' />
                  </th>
                  <th className='text-left'>
                    <Trans i18nKey='app.common.description' />
                  </th>
                  <th className='text-right'>
                    <Trans i18nKey='app.quote.history.table.sub_row.service_price' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {quote.quoteLines.map((line, lineIndex) => (
                  <>
                    {line.quoteServices.map((service, serviceIndex) => (
                      <tr key={`${lineIndex}-${service.code}`} className='py-2'>
                        {hasDiagnosis &&
                          serviceIndex === 0 &&
                          lineIndex === 0 && (
                            <td
                              className='py-2'
                              rowSpan={quote.quoteLines.reduce(
                                (acc, l) => acc + l.quoteServices.length,
                                0,
                              )}
                            >
                              <Button
                                id={`diagnosis-pdf-${quote.reference}`}
                                variant='link'
                                size='sm'
                                style={{ paddingLeft: 0 }}
                                onClick={() => {
                                  if (!quote.diagnosisId) return;
                                  setPdf(
                                    `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${selectedCustomerCode}/quote-diagnosis/${quote.diagnosisId}/print`,
                                  );
                                }}
                              >
                                <Flex alignItems='center' className='gap-2'>
                                  <Text weight='semi-bold'>
                                    {quote.diagnosisId}
                                  </Text>
                                  <Icon icon='download' size='xxs' />
                                </Flex>
                              </Button>
                            </td>
                          )}
                        <td className='py-2'>
                          <Text
                            id={`quote-service-code-${service.code}`}
                            weight='bold'
                          >
                            {service.code}
                          </Text>
                        </td>
                        <td className='py-2'>
                          <Text
                            id={`quote-service-name-${service.code}`}
                            weight='semi-bold'
                          >
                            {service.type === 'MOUNTING' ? (
                              <Trans i18nKey='app.common.quote.mounting' />
                            ) : (
                              service.name
                            )}
                          </Text>
                        </td>
                        <td align='right' className='py-2'>
                          <Text
                            id={`quote-service-price-${service.code}`}
                            weight='semi-bold'
                          >
                            {locale.formatters.money(service.totalPrice, {
                              currency: quote.currency,
                            })}
                          </Text>
                        </td>
                      </tr>
                    ))}
                    {lineIndex < quote.quoteLines.length - 1 && (
                      <tr>
                        <td colSpan={hasDiagnosis ? 4 : 3}>
                          <hr
                            className='primary'
                            style={{
                              width: '100%',
                              color: 'var(--primary-color)',
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </Col>
        <Col lg={4}>
          <div className='bg-white h-100'>
            <QuoteActions quote={quote} />
            <Flex className='w-100 mt-3' justifyContent='center'>
              <Text weight='bold' color='info'>
                <Trans i18nKey='app.quote.history.table.sub_row.offer.total_price_vat_incl.label' />
                {': '}
              </Text>
              <Text
                weight='bold'
                color='info'
                className='px-2'
                id='quote-total-included-tax'
              >
                {locale.formatters.money(
                  quote.quoteLines.reduce(
                    (acc, line) => acc + line.totalIncludedTax,
                    0,
                  ),
                  {
                    currency: quote.currency,
                  },
                )}
              </Text>
            </Flex>
          </div>
        </Col>
      </Row>
      {pdf !== null && <PdfModal url={pdf} onClose={() => setPdf(null)} />}
    </>
  );
};
